"use client";

// Save a laptop and hear about it when the price drops.
//
// One field, because every extra one costs sign-ups and we already know which
// laptop they were looking at. The address is remembered in this browser, so
// saving a second laptop is a single click.
//
// Nothing is sent to a new address beyond one confirmation email until that
// email is clicked. The modal says so plainly rather than promising alerts that
// will not arrive if the person never confirms.
import { c as _c } from "react/compiler-runtime";
import { useEffect, useState, useSyncExternalStore, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { API_BASE } from "@/lib/api";
import { WISHLIST_EVENT, isSaved, markSaved, readEmail, writeEmail } from "@/lib/wishlist-storage";
type Status = "idle" | "saving" | "done" | "error";

// Off unless explicitly switched on, and the button renders nothing at all while
// it is off.
//
// The reason for a flag rather than shipping it live: the API's mailer defaults
// to console mode, and it stays there until a sending domain is verified in
// Resend. A visitor who hands over an address, reads "check your inbox" and
// receives nothing is worse served than one who never saw the button — and they
// are the kind of visitor who does not come back. Turn this on in the same
// change that turns WISHLIST_EMAIL_MODE to live.
//
// Read at module scope because NEXT_PUBLIC_* values are inlined at build time;
// flipping it needs a rebuild, not just a restart.
const WISHLIST_ENABLED = process.env.NEXT_PUBLIC_WISHLIST_ENABLED === "true";

/** Defined once, outside the component: useSyncExternalStore resubscribes
 *  whenever this identity changes. */
function subscribeToWishlist(onChange: () => void) {
  window.addEventListener(WISHLIST_EVENT, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(WISHLIST_EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}
export function WishlistButton(t0) {
  const $ = _c(15);
  const {
    laptopConfigId,
    laptopName,
    country,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "pill" : t1;
  const [open, setOpen] = useState(false);
  let t2;
  if ($[0] !== country || $[1] !== laptopConfigId) {
    t2 = () => isSaved(laptopConfigId, country);
    $[0] = country;
    $[1] = laptopConfigId;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const saved = useSyncExternalStore(subscribeToWishlist, t2, _temp);
  if (!WISHLIST_ENABLED) {
    return null;
  }
  const label = saved ? "Saved" : "Save";
  let t3;
  if ($[3] !== label || $[4] !== saved || $[5] !== variant) {
    t3 = variant === "pill" ? <button type="button" onClick={() => setOpen(true)} aria-pressed={saved} title="Get an email when the price drops" className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold transition-colors ${saved ? "bg-brand text-brand-foreground ring-1 ring-brand" : "bg-card text-foreground ring-1 ring-border/60 hover:ring-foreground/30"}`}><IconBookmark filled={saved} />{label}</button> : <button type="button" onClick={() => setOpen(true)} aria-pressed={saved} className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl border border-border text-sm font-semibold text-foreground transition-colors hover:border-foreground/40 hover:bg-surface active:scale-[0.99]"><IconBookmark filled={saved} />{saved ? "Saved \u2014 we'll email you" : "Save & track the price"}</button>;
    $[3] = label;
    $[4] = saved;
    $[5] = variant;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== country || $[8] !== laptopConfigId || $[9] !== laptopName || $[10] !== open) {
    t4 = open && <WishlistModal laptopConfigId={laptopConfigId} laptopName={laptopName} country={country} onClose={() => setOpen(false)} onSaved={() => markSaved(laptopConfigId, country)} />;
    $[7] = country;
    $[8] = laptopConfigId;
    $[9] = laptopName;
    $[10] = open;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t3 || $[13] !== t4) {
    t5 = <>{t3}{t4}</>;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}
function _temp() {
  return false;
}
function WishlistModal({
  laptopConfigId,
  laptopName,
  country,
  onClose,
  onSaved
}: {
  laptopConfigId: string;
  laptopName: string;
  country: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Lazily initialised rather than filled in by an effect. The modal only ever
  // mounts from a click, so this always runs on the client and there is no
  // server render to disagree with.
  const [email, setEmail] = useState(readEmail);
  const [company, setCompany] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState("");
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);
  const submit = async (e_0: FormEvent) => {
    e_0.preventDefault();
    if (status === "saving") return;
    setStatus("saving");
    setError("");
    try {
      const res = await fetch(`${API_BASE}/wishlist/items`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          email,
          laptop_config_id: laptopConfigId,
          country,
          source_path: window.location.pathname,
          company
        })
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        setError(body?.detail ?? "That didn't save. Please try again.");
        setStatus("error");
        return;
      }
      writeEmail(email);
      onSaved();
      setStatus("done");
    } catch {
      setError("That didn't save. Please try again.");
      setStatus("error");
    }
  };

  // z-[60] puts this above the sellers drawer at z-50, which is one of the
  // places the button can be clicked from.
  return createPortal(<div className="fixed inset-0 z-[60] flex items-end justify-center p-4 sm:items-center" role="dialog" aria-modal="true" aria-label={`Get price alerts for ${laptopName}`}>
      <div className="absolute inset-0 bg-foreground/20 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />

      <div className="relative z-10 w-full max-w-sm overflow-hidden rounded-3xl bg-card ring-1 ring-border/60 shadow-2xl">
        <div className="flex items-start gap-3 border-b border-border/60 px-5 py-4">
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-bold leading-snug tracking-tight">
              {status === "done" ? "Check your inbox" : "Get price alerts"}
            </h3>
            <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{laptopName}</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="grid size-8 shrink-0 place-items-center rounded-full bg-surface text-muted-foreground transition-colors hover:bg-foreground hover:text-background">
            <svg viewBox="0 0 16 16" fill="currentColor" className="size-4">
              <path d="M3.72 3.72a.75.75 0 0 1 1.06 0L8 6.94l3.22-3.22a.75.75 0 1 1 1.06 1.06L9.06 8l3.22 3.22a.75.75 0 1 1-1.06 1.06L8 9.06l-3.22 3.22a.75.75 0 0 1-1.06-1.06L6.94 8 3.72 4.78a.75.75 0 0 1 0-1.06Z" />
            </svg>
          </button>
        </div>

        {status === "done" ? <div className="px-5 py-6">
            <p className="text-sm leading-relaxed text-foreground">
              We&apos;ve sent you a link to confirm. Click it and we&apos;ll email you when
              this laptop gets cheaper.
            </p>
            <button onClick={onClose} className="mt-5 flex h-11 w-full items-center justify-center rounded-2xl bg-brand text-sm font-semibold text-brand-foreground transition-all hover:opacity-95 active:scale-[0.99]">
              Done
            </button>
          </div> : <form onSubmit={submit} noValidate>
            <div className="px-5 py-5">
              <label htmlFor="wishlist-email" className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                Your email
              </label>
              <input id="wishlist-email" type="email" required autoFocus value={email} onChange={e_1 => setEmail(e_1.target.value)} placeholder="you@example.com" className="mt-2 h-11 w-full rounded-xl border border-border bg-surface px-3 text-sm text-foreground outline-none transition-colors focus:border-foreground/40" />

              {/* Honeypot: same field name as the contact and share-report
                  routes. Off-screen rather than display:none, which some bots
                  check for. */}
              <input type="text" name="company" value={company} onChange={e_2 => setCompany(e_2.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute left-[-9999px]" />

              {error && <p className="mt-2 text-[11px] text-red-600">{error}</p>}

              <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">
                We&apos;ll email you once to confirm, then only when the price drops.
                Unsubscribe any time.
              </p>
            </div>

            <div className="border-t border-border/60 px-5 py-4">
              <button type="submit" disabled={status === "saving" || !email} className="flex h-12 w-full items-center justify-center rounded-2xl bg-brand text-sm font-semibold text-brand-foreground shadow-sm transition-all hover:opacity-95 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-40">
                {status === "saving" ? "Saving…" : "Save this laptop"}
              </button>
            </div>
          </form>}
      </div>
    </div>, document.body);
}
function IconBookmark(t0) {
  const $ = _c(3);
  const {
    filled
  } = t0;
  const t1 = filled ? "currentColor" : "none";
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <path d="M4 2.5h8a.5.5 0 0 1 .5.5v10.2a.3.3 0 0 1-.47.25L8 10.8l-4.03 2.65a.3.3 0 0 1-.47-.25V3a.5.5 0 0 1 .5-.5Z" />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== t1) {
    t3 = <svg viewBox="0 0 16 16" className="size-3.5 shrink-0" fill={t1} stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round">{t2}</svg>;
    $[1] = t1;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  return t3;
}